import { useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { ConfirmDialog } from '../components/feedback/ConfirmDialog';
import {
  useWithdrawalAccounts,
  type WithdrawalAccountDTO,
  type WithdrawalAccountPayload,
} from '../features/withdrawal-accounts';
import { formatDateTime } from '../lib/format';

const PAGE_SIZE_OPTIONS = [20, 50, 100];

const STATUS_LABELS: Record<string, string> = {
  active: '正常',
  disabled: '已禁用',
  deleted: '已删除',
};

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-50 text-emerald-700',
  disabled: 'bg-amber-50 text-amber-700',
  deleted: 'bg-gray-100 text-gray-500',
};

interface EditTarget {
  account?: WithdrawalAccountDTO;
  userId?: number;
  accountName: string;
  alipayAccount: string;
  bankCode: string;
  bankName: string;
  bankCardNumber: string;
  bankBranch: string;
}

function maskFallback(value: string | undefined): string {
  return value || '-';
}

function userLabel(item: WithdrawalAccountDTO): string {
  return item.email || item.nickname || `用户 ${item.user_id}`;
}

export function WithdrawalAccountsPage() {
  const {
    marketId,
    items,
    total,
    page,
    pageSize,
    totalPages,
    userId,
    keyword,
    accountKeyword,
    nameKeyword,
    status,
    loading,
    error,
    submitting,
    setFilter,
    setPage,
    setPageSize,
    resetFilters,
    reload,
    saveAccount,
    deleteAccount,
    updateAccountStatus,
  } = useWithdrawalAccounts();

  const [editTarget, setEditTarget] = useState<EditTarget | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<WithdrawalAccountDTO | null>(null);
  const [statusTarget, setStatusTarget] = useState<WithdrawalAccountDTO | null>(null);

  const numericUserId = useMemo(() => {
    const parsed = Number(userId);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
  }, [userId]);

  const openEdit = (item: WithdrawalAccountDTO) => {
    setEditTarget({
      account: item,
      accountName: item.account_name ?? '',
      alipayAccount: item.alipay_account ?? '',
      bankCode: item.bank_code ?? '',
      bankName: item.bank_name ?? '',
      bankCardNumber: item.bank_card_number ?? '',
      bankBranch: item.bank_branch ?? '',
    });
  };

  const openUserUpsert = () => {
    if (!numericUserId) return;
    const activeAccount = items.find((item) => item.user_id === numericUserId && item.status === 'active');
    setEditTarget({
      account: activeAccount,
      userId: numericUserId,
      accountName: activeAccount?.account_name ?? '',
      alipayAccount: activeAccount?.alipay_account ?? '',
      bankCode: activeAccount?.bank_code ?? '',
      bankName: activeAccount?.bank_name ?? '',
      bankCardNumber: activeAccount?.bank_card_number ?? '',
      bankBranch: activeAccount?.bank_branch ?? '',
    });
  };

  const updateEditField = (field: 'accountName' | 'alipayAccount' | 'bankCode' | 'bankName' | 'bankCardNumber' | 'bankBranch', value: string) => {
    setEditTarget((current) => current ? { ...current, [field]: value } : current);
  };

  const submitEdit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!editTarget) return;
    const payload: WithdrawalAccountPayload = {
      market_id: editTarget.account?.market_id || marketId,
      type: editTarget.account?.type || 'alipay',
      bank_code: editTarget.bankCode.trim(),
      bank_name: editTarget.bankName.trim(),
      bank_card_number: editTarget.bankCardNumber.trim(),
      bank_branch: editTarget.bankBranch.trim(),
      account_name: editTarget.accountName.trim(),
      alipay_account: editTarget.alipayAccount.trim(),
    };
    const target = editTarget.account ?? (editTarget.userId ? { user_id: editTarget.userId } : null);
    if (!target) return;
    const ok = await saveAccount(target, payload);
    if (ok) setEditTarget(null);
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    const ok = await deleteAccount(deleteTarget.id);
    if (ok) setDeleteTarget(null);
  };

  const confirmStatusChange = async () => {
    if (!statusTarget) return;
    const nextStatus = statusTarget.status === 'disabled' ? 'active' : 'disabled';
    const ok = await updateAccountStatus(statusTarget.id, { status: nextStatus });
    if (ok) setStatusTarget(null);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 border-2 border-gray-300 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-20 text-center">
        <p className="mb-3 text-sm text-red-500">{error}</p>
        <button onClick={reload} className="text-sm text-primary hover:underline">
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <label className="text-sm text-gray-700">
          地区
          <select value={marketId} onChange={(event) => setFilter('marketId', event.target.value)}
            className="ml-2 h-9 rounded-md border border-gray-300 px-3 text-sm">
            <option value="CN">中国</option>
            <option value="VN">越南</option>
          </select>
        </label>
        <label className="text-sm text-gray-700">
          用户 ID
          <input
            value={userId}
            onChange={(event) => setFilter('userId', event.target.value)}
            className="ml-2 h-9 w-32 rounded-md border border-gray-300 px-3 text-sm tabular-nums focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          用户关键字
          <input
            value={keyword}
            onChange={(event) => setFilter('keyword', event.target.value)}
            placeholder="邮箱/昵称"
            className="ml-2 h-9 w-44 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          {marketId === 'VN' ? '银行卡号' : '支付宝账号'}
          <input
            value={accountKeyword}
            onChange={(event) => setFilter('accountKeyword', event.target.value)}
            className="ml-2 h-9 w-44 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          姓名
          <input
            value={nameKeyword}
            onChange={(event) => setFilter('nameKeyword', event.target.value)}
            className="ml-2 h-9 w-36 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          />
        </label>
        <label className="text-sm text-gray-700">
          状态
          <select
            value={status}
            onChange={(event) => setFilter('status', event.target.value)}
            className="ml-2 h-9 rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
          >
            <option value="">正常</option>
            <option value="active">正常</option>
            <option value="disabled">已禁用</option>
            <option value="deleted">已删除</option>
            <option value="all">全部</option>
          </select>
        </label>
        <button
          onClick={resetFilters}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          重置
        </button>
        <button
          onClick={reload}
          className="h-9 rounded-md border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50"
        >
          刷新
        </button>
        {numericUserId && marketId === 'CN' && (
          <button
            onClick={openUserUpsert}
            className="h-9 rounded-md bg-primary px-3 text-sm font-medium text-white hover:bg-primary/90"
          >
            绑定/更新该用户账号
          </button>
        )}
      </div>

      <div className="overflow-hidden rounded-md border border-gray-200 bg-white">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50 text-xs text-gray-500">
            <tr>
              <th className="px-4 py-3 text-left font-medium">用户</th>
              <th className="px-4 py-3 text-left font-medium">姓名</th>
              <th className="px-4 py-3 text-left font-medium">{marketId === 'VN' ? '银行账户' : '支付宝账号'}</th>
              <th className="px-4 py-3 text-left font-medium">状态</th>
              <th className="px-4 py-3 text-left font-medium">更新时间</th>
              <th className="px-4 py-3 text-left font-medium">禁用/删除时间</th>
              <th className="px-4 py-3 text-right font-medium">操作</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <Link to={`/users/${item.user_id}`} className="font-medium text-primary hover:underline">
                    {userLabel(item)}
                  </Link>
                  <div className="mt-0.5 text-xs tabular-nums text-gray-400">ID {item.user_id}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="text-gray-900">{item.account_name || '-'}</div>
                  <div className="mt-0.5 text-xs text-gray-400">{maskFallback(item.masked_account_name)}</div>
                </td>
                <td className="px-4 py-3">
                  <div className="mb-1 text-xs text-gray-500">{item.market_id === 'VN' ? '越南' : item.market_id === 'CN' ? '中国' : item.market_id} · {item.type === 'bank_card' ? '银行卡' : '支付宝'}</div>
                  {item.type === 'bank_card' && <div className="text-gray-700">{item.bank_name || item.bank_code || '-'}{item.bank_branch ? ` · ${item.bank_branch}` : ''}</div>}
                  <div className="text-gray-900">{(item.type === 'bank_card' ? item.bank_card_number : item.alipay_account) || '-'}</div>
                  <div className="mt-0.5 text-xs text-gray-400">{maskFallback((item.type === 'bank_card' ? item.masked_bank_card_number : item.masked_alipay_account) || item.target_masked)}</div>
                </td>
                <td className="px-4 py-3">
                  <span className={`inline-flex rounded-full px-2.5 py-1 text-xs font-medium ${STATUS_STYLES[item.status] ?? 'bg-gray-100 text-gray-600'}`}>
                    {STATUS_LABELS[item.status] ?? item.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{item.updated_at ? formatDateTime(item.updated_at) : '-'}</td>
                <td className="px-4 py-3 text-gray-600">
                  {item.disabled_at ? formatDateTime(item.disabled_at) : item.deleted_at ? formatDateTime(item.deleted_at) : '-'}
                </td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => openEdit(item)}
                      disabled={item.status === 'deleted'}
                      className="text-sm text-primary hover:underline disabled:text-gray-400 disabled:no-underline"
                    >
                      编辑
                    </button>
                    <button
                      type="button"
                      onClick={() => setStatusTarget(item)}
                      disabled={item.status === 'deleted'}
                      className={`text-sm hover:underline disabled:text-gray-400 disabled:no-underline ${
                        item.status === 'disabled' ? 'text-emerald-600' : 'text-amber-600'
                      }`}
                    >
                      {item.status === 'disabled' ? '启用' : '禁用'}
                    </button>
                    <button
                      type="button"
                      onClick={() => setDeleteTarget(item)}
                      disabled={item.status === 'deleted'}
                      className="text-sm text-red-500 hover:underline disabled:text-gray-400 disabled:no-underline"
                    >
                      删除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-4 py-12 text-center text-gray-400">
                  暂无提现账号
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
        <span>共 {total} 条</span>
        <div className="flex items-center gap-2">
          <select
            value={pageSize}
            onChange={(event) => setPageSize(Number(event.target.value))}
            className="h-8 rounded-md border border-gray-300 px-2 text-sm"
          >
            {PAGE_SIZE_OPTIONS.map((value) => (
              <option key={value} value={value}>{value} / 页</option>
            ))}
          </select>
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page <= 1}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            上一页
          </button>
          <span className="tabular-nums">{page} / {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page >= totalPages}
            className="h-8 rounded-md border border-gray-300 px-3 disabled:opacity-50"
          >
            下一页
          </button>
        </div>
      </div>

      {editTarget && (
        <div className="fixed inset-0 z-40 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/30"
            onClick={submitting ? undefined : () => setEditTarget(null)}
          />
          <form onSubmit={submitEdit} className="relative mx-4 w-full max-w-md max-h-[90vh] overflow-y-auto space-y-4 rounded-lg bg-white p-6 shadow-lg">
            <div>
              <h3 className="text-base font-medium text-gray-900">{editTarget.account?.type === 'bank_card' ? '编辑银行卡提现账号' : '编辑支付宝提现账号'}</h3>
              <p className="mt-1 text-sm text-gray-500">
                修改当前绑定信息。已提交的提现单继续使用提交时保存的账号快照。
              </p>
            </div>
            <label className="block text-sm font-medium text-gray-700">
              姓名
              <input
                value={editTarget.accountName}
                onChange={(event) => updateEditField('accountName', event.target.value)}
                required
                className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
              />
            </label>
            {editTarget.account?.type === 'bank_card' ? (
              <>
                {([
                  ['bankCode', '银行代码', true],
                  ['bankName', '银行名称', true],
                  ['bankCardNumber', '银行卡号', true],
                  ['bankBranch', '开户支行', false],
                ] as const).map(([field, label, required]) => (
                  <label key={field} className="block text-sm font-medium text-gray-700">
                    {label}
                    <input value={editTarget[field]} onChange={(event) => updateEditField(field, event.target.value)} required={required}
                      className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50" />
                  </label>
                ))}
              </>
            ) : (
              <label className="block text-sm font-medium text-gray-700">
                支付宝账号
                <input
                  value={editTarget.alipayAccount}
                  onChange={(event) => updateEditField('alipayAccount', event.target.value)}
                  required
                  className="mt-1 h-10 w-full rounded-md border border-gray-300 px-3 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </label>
            )}
            <div className="flex justify-end gap-3 pt-1">
              <button
                type="button"
                onClick={() => setEditTarget(null)}
                disabled={submitting}
                className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                取消
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90 disabled:opacity-40"
              >
                {submitting && <span className="h-3.5 w-3.5 rounded-full border-2 border-white/40 border-t-white animate-spin" />}
                保存
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="删除提现账号"
        description="删除后该账号会被逻辑删除，用户需要重新绑定后才能继续使用。已提交的提现单会保留提交时的账号快照，不会随本次删除改变。"
        confirmLabel="删除"
        variant="danger"
        loading={submitting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={Boolean(statusTarget)}
        title={statusTarget?.status === 'disabled' ? '启用提现账号' : '禁用提现账号'}
        description={
          statusTarget?.status === 'disabled'
            ? '启用后该提现账号可重新作为用户当前有效提现账号使用，但不会自动恢复已被禁用的用户。'
            : '禁用后，同一收款账号当前已绑定的账号会同步禁用，对应用户也会被禁用；后续用户再绑定该收款账号时会保存绑定记录并立即禁用用户。'
        }
        confirmLabel={statusTarget?.status === 'disabled' ? '启用' : '禁用'}
        variant={statusTarget?.status === 'disabled' ? 'primary' : 'danger'}
        loading={submitting}
        onConfirm={confirmStatusChange}
        onCancel={() => setStatusTarget(null)}
      />
    </div>
  );
}
